import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { canViewContext } from '@/lib/auth/access';
import { useCurrentRole } from '@/lib/store/auth';
import { type Context, useUiStore } from '@/lib/store/ui';
import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ContextToggles } from './ContextToggles';
import { useFilterSummary } from './filterSummary';

/** Contexts whose topbar carries filter pills (everything else — Flights,
 *  Apps — renders none, so there is nothing to collapse). */
const CONTEXTS_WITH_TOGGLES: readonly Context[] = [
  'drones',
  'oi',
  'surv',
  'infra',
  'rtm',
  'inv',
  'ds',
];

/** ENG-318 — below `xl` the filter pills no longer fit beside the logo: on a
 *  phone they wrapped onto one line each and grew the bar to a third of the
 *  screen, and a tablet or small laptop still can't hold a full row (the OI tab
 *  alone has 11 chips). Here they collapse behind one "Filters" button into a
 *  top sheet instead. The inline pills in `Topbar` take over again from `xl`. */
export function MobileFilters() {
  const ctx = useUiStore((s) => s.activeContext);
  const role = useCurrentRole();
  const summary = useFilterSummary(ctx);
  const [open, setOpen] = useState(false);

  // Growing past `xl` hands the pills back to the bar; don't leave a
  // modal sheet open over a layout that no longer needs it.
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1280px)');
    if (!mq) return;
    const close = () => mq.matches && setOpen(false);
    mq.addEventListener('change', close);
    return () => mq.removeEventListener('change', close);
  }, []);

  // Same gate as the inline pills: a locked context shows the login gate, so
  // its filters would be dead controls.
  if (!CONTEXTS_WITH_TOGGLES.includes(ctx) || !canViewContext(ctx, role)) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="xl:hidden h-9 gap-1.5" aria-label="Filters">
          <SlidersHorizontal size={14} />
          <span className="hidden min-[340px]:inline">Filters</span>
          {summary && (
            <span
              title={`${summary.on} of ${summary.total} filters on`}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-(--color-selected) px-1.5 text-[0.692em] font-semibold tabular-nums text-(--color-selected-fg)"
            >
              {summary.on}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="top" title="Filters" className="max-h-[80dvh] overflow-y-auto p-4 pt-12">
        {/* Pills stay 30 px on desktop; give them a thumb-sized target here. */}
        <div className="[&_button]:min-h-11">
          <ContextToggles />
        </div>
      </SheetContent>
    </Sheet>
  );
}
